import React, { useState, useEffect, useCallback, useRef } from 'react';

function getCsrfToken() {
  var meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function CheckIcon({ size }) {
  return (
    <svg width={size || 14} height={size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

var STEP_ICONS = ['zmdi-smartphone', 'zmdi-crop-free', 'zmdi-key', 'zmdi-shield-check'];

function TwoFactorWizard(props) {
  var { statusUrl, setupUrl, verifyUrl, disableUrl, backupCodesUrl, regenerateBackupCodesUrl, forced, continueUrl, translations } = props;

  var [loading, setLoading] = useState(true);
  var [enabled, setEnabled] = useState(false);
  var [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  var [step, setStep] = useState(1);
  var [stepDirection, setStepDirection] = useState('forward');
  var [qrSvg, setQrSvg] = useState('');
  var [secretKey, setSecretKey] = useState('');
  var [code, setCode] = useState('');
  var [backupCodes, setBackupCodes] = useState([]);
  var [error, setError] = useState(null);
  var [submitting, setSubmitting] = useState(false);
  var [copied, setCopied] = useState(false);
  var [showDisableModal, setShowDisableModal] = useState(false);
  var [disableCode, setDisableCode] = useState('');
  var [showRegenerateModal, setShowRegenerateModal] = useState(false);
  var [regenerateCode, setRegenerateCode] = useState('');
  var [showBackupModal, setShowBackupModal] = useState(false);

  var disableModalRef = useRef(null);
  var regenerateModalRef = useRef(null);
  var backupModalRef = useRef(null);

  var fetchStatus = useCallback(function () {
    fetch(statusUrl, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.success) {
          setEnabled(data.data.enabled);
          setBackupCodesRemaining(data.data.backup_codes_remaining);
        }
        setLoading(false);
      })
      .catch(function () { setLoading(false); });
  }, [statusUrl]);

  useEffect(function () { fetchStatus(); }, [fetchStatus]);

  var jsonPost = useCallback(function (url, method, body) {
    return fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'Accept': 'application/json'
      },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) { return r.json(); });
  }, []);

  var goToStep = useCallback(function (nextStep) {
    setStepDirection(nextStep > step ? 'forward' : 'backward');
    setStep(nextStep);
  }, [step]);

  var handleSetup = useCallback(function () {
    setSubmitting(true);
    setError(null);
    jsonPost(setupUrl, 'POST')
      .then(function (data) {
        if (data.success) {
          setQrSvg(data.data.qr_svg);
          setSecretKey(data.data.secret_key);
          goToStep(2);
        } else {
          setError(data.message || data.errors || 'Setup failed');
        }
        setSubmitting(false);
      })
      .catch(function () { setError('Setup failed'); setSubmitting(false); });
  }, [setupUrl, jsonPost, goToStep]);

  var handleVerify = useCallback(function () {
    if (code.length !== 6) return;
    setSubmitting(true);
    setError(null);
    jsonPost(verifyUrl, 'POST', { code: code })
      .then(function (data) {
        if (data.success) {
          setBackupCodes(data.data.backup_codes);
          goToStep(4);
          setEnabled(true);
        } else {
          setError(data.message || data.errors || translations.invalid_code);
        }
        setSubmitting(false);
      })
      .catch(function () { setError(translations.invalid_code); setSubmitting(false); });
  }, [code, verifyUrl, jsonPost, translations, goToStep]);

  var handleDisable = useCallback(function () {
    if (disableCode.length < 6) return;
    setSubmitting(true);
    setError(null);
    jsonPost(disableUrl, 'DELETE', { code: disableCode })
      .then(function (data) {
        if (data.success) {
          setEnabled(false);
          if (disableModalRef.current && window.$) {
            window.$(disableModalRef.current).modal('hide');
          }
          setDisableCode('');
          goToStep(1);
          setBackupCodes([]);
          setQrSvg('');
          setSecretKey('');
          setCode('');
        } else {
          setError(data.message || data.errors || translations.invalid_code);
        }
        setSubmitting(false);
      })
      .catch(function () { setError(translations.invalid_code); setSubmitting(false); });
  }, [disableCode, disableUrl, jsonPost, translations, goToStep]);

  var handleRegenerate = useCallback(function () {
    if (regenerateCode.length !== 6) return;
    setSubmitting(true);
    setError(null);
    jsonPost(regenerateBackupCodesUrl, 'POST', { code: regenerateCode })
      .then(function (data) {
        if (data.success) {
          setBackupCodes(data.data.backup_codes);
          if (regenerateModalRef.current && window.$) {
            window.$(regenerateModalRef.current).modal('hide');
          }
          setRegenerateCode('');
          setShowBackupModal(true);
          fetchStatus();
        } else {
          setError(data.message || data.errors || translations.invalid_code);
        }
        setSubmitting(false);
      })
      .catch(function () { setError(translations.invalid_code); setSubmitting(false); });
  }, [regenerateCode, regenerateBackupCodesUrl, jsonPost, translations, fetchStatus]);

  var copyToClipboard = useCallback(function (text) {
    navigator.clipboard.writeText(text).then(function () {
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 2000);
    });
  }, []);

  var handleCodeChange = useCallback(function (e) {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
  }, []);

  // Bootstrap modal effects
  useEffect(function () {
    if (showDisableModal && disableModalRef.current && window.$) {
      var $modal = window.$(disableModalRef.current);
      $modal.modal({ backdrop: 'static', keyboard: true });
      $modal.modal('show');
      var handleHidden = function () {
        setShowDisableModal(false);
        setDisableCode('');
        setError(null);
        setSubmitting(false);
      };
      $modal.on('hidden.bs.modal', handleHidden);
      return function () { $modal.off('hidden.bs.modal', handleHidden); };
    }
  }, [showDisableModal]);

  useEffect(function () {
    if (showRegenerateModal && regenerateModalRef.current && window.$) {
      var $modal = window.$(regenerateModalRef.current);
      $modal.modal({ backdrop: 'static', keyboard: true });
      $modal.modal('show');
      var handleHidden = function () {
        setShowRegenerateModal(false);
        setRegenerateCode('');
        setError(null);
        setSubmitting(false);
      };
      $modal.on('hidden.bs.modal', handleHidden);
      return function () { $modal.off('hidden.bs.modal', handleHidden); };
    }
  }, [showRegenerateModal]);

  useEffect(function () {
    if (showBackupModal && backupModalRef.current && window.$) {
      var $modal = window.$(backupModalRef.current);
      $modal.modal({ backdrop: 'static', keyboard: true });
      $modal.modal('show');
      var handleHidden = function () {
        setShowBackupModal(false);
        setBackupCodes([]);
      };
      $modal.on('hidden.bs.modal', handleHidden);
      return function () { $modal.off('hidden.bs.modal', handleHidden); };
    }
  }, [showBackupModal]);

  // Stepper
  var stepLabels = [
    translations.stepper_install,
    translations.stepper_scan,
    translations.stepper_verify,
    translations.stepper_backup
  ];

  function renderStepper() {
    var elements = [];

    for (var i = 0; i < 4; i++) {
      var stepNum = i + 1;
      var isCompleted = step > stepNum;
      var isActive = step === stepNum;
      var stateClass = isCompleted ? 'twofa-stepper__step--completed' :
                       isActive ? 'twofa-stepper__step--active' :
                       'twofa-stepper__step--pending';

      elements.push(
        <div key={'step-' + i} className={'twofa-stepper__step ' + stateClass}>
          <div className="twofa-stepper__icon">
            {isCompleted ? <CheckIcon /> : <i className={'zmdi ' + STEP_ICONS[i]} />}
          </div>
          <span className="twofa-stepper__label">{stepLabels[i]}</span>
        </div>
      );

      if (i < 3) {
        var connectorCompleted = step > stepNum;
        elements.push(
          <div
            key={'conn-' + i}
            className={'twofa-stepper__connector' + (connectorCompleted ? ' twofa-stepper__connector--completed' : '')}
          />
        );
      }
    }

    return <div className="twofa-stepper">{elements}</div>;
  }

  function stepTitle(num, titleText) {
    return translations.step_prefix.replace('%{number}', num) + ' ' + titleText;
  }

  function finishWizard() {
    setStep(1);
    setBackupCodes([]);
    fetchStatus();
  }

  // Loading
  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border spinner-border-sm text-muted" />
      </div>
    );
  }

  // ─── Enabled state ─────────────────────────────────────────────────────────
  if (enabled && step !== 4) {
    return (
      <div className="twofa-status">
        <div className="twofa-status__header">
          <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-lg-between w-100">
            <div className="d-flex align-items-start mb-3 mb-lg-0">
              <div className="twofa-status__icon mr-3">
                <i className="zmdi zmdi-shield-check" />
              </div>
              <div>
                <h5 className="mb-2">{translations.title}</h5>
                <p className="text-muted mb-0">{translations.active_message}</p>
              </div>
            </div>
            <span className="twofa-status__badge twofa-status__badge--enabled">
              {translations.status_enabled}
            </span>
          </div>
        </div>
        <div className="twofa-status__info">
          <i className="zmdi zmdi-key text-muted" />
          <small className="text-muted mb-0">
            {translations.backup_remaining.replace('%{count}', backupCodesRemaining)}
          </small>
        </div>
        <div className="twofa-status__actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={function () { setShowRegenerateModal(true); setError(null); }}
          >
            <i className="zmdi zmdi-refresh-alt mr-1" />
            {translations.regenerate_backup}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={function () { setShowDisableModal(true); setError(null); }}
          >
            <i className="zmdi zmdi-lock-open mr-1" />
            {translations.disable_button}
          </button>
        </div>

        {showDisableModal && (
          <div ref={disableModalRef} className="modal fade twofa-modal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{translations.disable_button}</h5>
                </div>
                <div className="modal-body">
                  <p className="text-muted">{translations.confirm_disable}</p>
                  {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      value={disableCode}
                      onChange={function (e) { setDisableCode(e.target.value.replace(/[^0-9a-f-]/gi, '').slice(0, 9)); }}
                      placeholder="000000"
                      autoFocus
                      inputMode="numeric"
                    />
                    <i className="form-group__bar" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-link text-muted"
                    onClick={function () { if (disableModalRef.current && window.$) window.$(disableModalRef.current).modal('hide'); }}
                  >
                    {translations.cancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDisable}
                    disabled={submitting || disableCode.length < 6}
                  >
                    {submitting ? translations.verifying : translations.disable_button}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRegenerateModal && (
          <div ref={regenerateModalRef} className="modal fade twofa-modal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{translations.regenerate_backup}</h5>
                </div>
                <div className="modal-body">
                  <p className="text-muted">{translations.confirm_regenerate}</p>
                  {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      value={regenerateCode}
                      onChange={function (e) { setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
                      placeholder="000000"
                      autoFocus
                      inputMode="numeric"
                    />
                    <i className="form-group__bar" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-link text-muted"
                    onClick={function () { if (regenerateModalRef.current && window.$) window.$(regenerateModalRef.current).modal('hide'); }}
                  >
                    {translations.cancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRegenerate}
                    disabled={submitting || regenerateCode.length !== 6}
                  >
                    {submitting ? translations.verifying : translations.regenerate_backup}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showBackupModal && (
          <div ref={backupModalRef} className="modal fade twofa-modal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{translations.step_backup_title}</h5>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning py-2 mb-3">
                    <strong>{translations.backup_warning_title}</strong><br />
                    {translations.backup_warning}
                  </div>
                  <div className="twofa-backup__container">
                    <div className="twofa-backup__grid">
                      {backupCodes.map(function (c, i) {
                        return (
                          <div key={i} className="twofa-backup__code">
                            <span className="twofa-backup__code-num">{i + 1}.</span>
                            <code>{c}</code>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={function () { copyToClipboard(backupCodes.join('\n')); }}
                  >
                    <CopyIcon />{' '}
                    {copied ? translations.backup_copied : translations.copy_all}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={function () { if (backupModalRef.current && window.$) window.$(backupModalRef.current).modal('hide'); }}
                  >
                    {translations.done_button}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Wizard ────────────────────────────────────────────────────────────────
  var apps = [
    {
      name: translations.google_auth,
      desc: translations.google_auth_desc,
      icon: 'zmdi zmdi-smartphone zmdi-hc-lg',
      iosUrl: translations.google_auth_ios,
      androidUrl: translations.google_auth_android
    },
    {
      name: translations.microsoft_auth,
      desc: translations.microsoft_auth_desc,
      icon: 'zmdi zmdi-windows zmdi-hc-lg',
      iosUrl: translations.microsoft_auth_ios,
      androidUrl: translations.microsoft_auth_android
    },
    {
      name: translations.authy,
      desc: translations.authy_desc,
      icon: 'zmdi zmdi-smartphone-iphone zmdi-hc-lg',
      iosUrl: translations.authy_ios,
      androidUrl: translations.authy_android
    }
  ];

  return (
    <div className="twofa-wizard">
      <h5 className="twofa-wizard__title">{translations.title}</h5>
      {renderStepper()}

      <div className="twofa-wizard__content">
        {/* Step 1: Install App */}
        {step === 1 && (
          <div className={'twofa-wizard__step-content twofa-wizard__step-content--' + stepDirection}>
            <h5 className="twofa-wizard__step-title">
              {stepTitle('1', translations.step_install_title)}
            </h5>
            <p className="text-muted mb-3">{translations.step_install_description}</p>
            {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
            <div className="twofa-apps">
              {apps.map(function (app, i) {
                return (
                  <div key={i} className="twofa-apps__card">
                    <div className="twofa-apps__card-icon">
                      <i className={app.icon} />
                    </div>
                    <div className="twofa-apps__card-body d-flex flex-column">
                      <div>
                        <strong>{app.name}</strong>
                        <small className="text-muted">{app.desc}</small>
                      </div>
                      <div className="twofa-apps__card-links">
                        <a href={app.iosUrl} target="_blank" rel="noopener noreferrer" className="twofa-apps__link">
                          <i className="zmdi zmdi-apple" />{translations.ios_label}
                        </a>
                        <a href={app.androidUrl} target="_blank" rel="noopener noreferrer" className="twofa-apps__link">
                          <i className="zmdi zmdi-android" />{translations.android_label}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="twofa-wizard__footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSetup}
                disabled={submitting}
              >
                {submitting && <span className="spinner-border spinner-border-sm mr-1" />}
                {translations.next}
                {!submitting && <i className="zmdi zmdi-arrow-right ml-1" />}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Scan QR Code */}
        {step === 2 && (
          <div className={'twofa-wizard__step-content twofa-wizard__step-content--' + stepDirection}>
            <h5 className="twofa-wizard__step-title">
              {stepTitle('2', translations.step_scan_title)}
            </h5>
            <p className="text-muted mb-3">{translations.step_scan_description}</p>
            <div className="row align-items-stretch">
              <div className="col-lg-4 mb-4 mb-lg-0">
                <div className="twofa-qr">
                  <div className="twofa-qr__svg" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                </div>
              </div>
              <div className="col-lg-8">
                <div className="d-flex flex-column h-100">
                  <div className="twofa-manual">
                    <div className="twofa-manual__label">
                      {translations.manual_entry}
                    </div>
                    <div className="twofa-manual__key-box">
                      <code className="twofa-manual__key">{secretKey}</code>
                      <button
                        type="button"
                        className="twofa-manual__copy"
                        onClick={function () { copyToClipboard(secretKey); }}
                        title={copied ? translations.copied : translations.copy_key}
                        aria-label={copied ? translations.copied : translations.copy_key}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                  <div className="alert alert-warning mt-3 mb-0">
                    <strong>{translations.totp_important}</strong><br />
                    {translations.totp_notice}
                  </div>
                </div>
              </div>
            </div>
            <div className="twofa-wizard__footer">
              <button type="button" className="btn btn-secondary" onClick={function () { goToStep(1); }}>
                <i className="zmdi zmdi-arrow-left mr-1" />
                {translations.back}
              </button>
              <button type="button" className="btn btn-primary" onClick={function () { goToStep(3); }}>
                {translations.scanned_button}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Verify Code */}
        {step === 3 && (
          <div className={'twofa-wizard__step-content twofa-wizard__step-content--' + stepDirection}>
            <h5 className="twofa-wizard__step-title">
              {stepTitle('3', translations.step_verify_title)}
            </h5>
            <p className="text-muted mb-3">{translations.step_verify_description}</p>
            {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
            <div className="row">
              <div className="col-lg-6 col-xl-5">
                <div className="d-flex flex-column">
                  <div className="twofa-verify__label">{translations.verify_label}</div>
                  <div className="twofa-verify__input-wrap">
                    <i className="zmdi zmdi-key twofa-verify__input-icon" />
                    <div className="form-group mb-0 flex-grow-1">
                      <input
                        type="text"
                        className="form-control twofa-verify__input"
                        value={code}
                        onChange={handleCodeChange}
                        placeholder={translations.verify_placeholder}
                        maxLength={6}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        autoFocus
                      />
                      <i className="form-group__bar" />
                    </div>
                  </div>
                  <div className="twofa-verify__hint mt-3" dangerouslySetInnerHTML={{ __html: translations.verify_hint }} />
                </div>
              </div>
            </div>
            <div className="twofa-wizard__footer">
              <button type="button" className="btn btn-secondary" onClick={function () { goToStep(2); setError(null); }}>
                <i className="zmdi zmdi-arrow-left mr-1" />
                {translations.back}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleVerify}
                disabled={submitting || code.length !== 6}
              >
                {submitting ? translations.verifying : translations.verify_button}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Backup Codes */}
        {step === 4 && (
          <div className={'twofa-wizard__step-content twofa-wizard__step-content--' + stepDirection}>
            <h5 className="twofa-wizard__step-title">
              {stepTitle('4', translations.step_backup_title)}
            </h5>
            <p className="text-muted mb-3">{translations.step_backup_description}</p>
            <div className="d-flex flex-column">
              <div className="twofa-backup__container">
                <div className="twofa-backup__grid">
                  {backupCodes.map(function (c, i) {
                    return (
                      <div key={i} className="twofa-backup__code">
                        <span className="twofa-backup__code-num">{i + 1}.</span>
                        <code>{c}</code>
                      </div>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm align-self-start mt-3"
                onClick={function () { copyToClipboard(backupCodes.join('\n')); }}
              >
                <CopyIcon />{' '}
                {copied ? translations.backup_copied : translations.copy_all}
              </button>
              <div className="alert alert-danger mt-3 mb-0">
                <strong>{translations.backup_warning_title}</strong><br />
                {translations.backup_warning}
              </div>
            </div>
            <div className="twofa-wizard__footer">
              {forced ? (
                <button type="button" className="btn btn-primary" onClick={function () { window.location.href = continueUrl; }}>
                  <CheckIcon size={16} />{' '}
                  {translations.continue_button}
                </button>
              ) : (
                <React.Fragment>
                  <button type="button" className="btn btn-secondary" onClick={finishWizard}>
                    <i className="zmdi zmdi-arrow-left mr-1" />
                    {translations.back}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={finishWizard}>
                    <CheckIcon size={16} />{' '}
                    {translations.done_button}
                  </button>
                </React.Fragment>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TwoFactorWizard;
